import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import accountVerificationService from '../../../services/accountVerificationService';

const AuditLogPanel = ({ refreshTrigger }) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    actionType: '',
    email: '',
    dateFrom: '',
    dateTo: ''
  });

  useEffect(() => {
    loadAuditLogs();
  }, [refreshTrigger]);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await accountVerificationService?.getVerificationAuditLogs(filters);
      setAuditLogs(data);
    } catch (err) {
      setError(err?.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    loadAuditLogs();
  };

  const handleClearFilters = () => {
    setFilters({
      actionType: '',
      email: '',
      dateFrom: '',
      dateTo: ''
    });
    setTimeout(() => loadAuditLogs(), 100);
  };

  const getActionIcon = (actionType) => {
    const icons = {
      email_sent: 'Mail',
      email_failed: 'XCircle',
      manual_verification: 'UserCheck',
      resend_email: 'RefreshCw',
      token_expired: 'Clock'
    };
    return icons?.[actionType] || 'Activity';
  };

  const getActionColor = (actionType) => {
    const colors = {
      email_sent: 'text-blue-600 bg-blue-50 dark:bg-blue-900/30',
      email_failed: 'text-red-600 bg-red-50 dark:bg-red-900/30',
      manual_verification: 'text-green-600 bg-green-50 dark:bg-green-900/30',
      resend_email: 'text-purple-600 bg-purple-50 dark:bg-purple-900/30',
      token_expired: 'text-amber-600 bg-amber-50 dark:bg-amber-900/30'
    };
    return colors?.[actionType] || 'text-gray-600 bg-gray-50 dark:bg-gray-800';
  };

  if (loading && auditLogs?.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-muted/50 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="Filter" className="w-4 h-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold text-foreground">Filters</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Select
            value={filters?.actionType}
            onChange={(e) => handleFilterChange('actionType', e?.target?.value)}
          >
            <option value="">All Actions</option>
            <option value="email_sent">Email Sent</option>
            <option value="email_failed">Email Failed</option>
            <option value="manual_verification">Manual Verification</option>
            <option value="resend_email">Resend Email</option>
            <option value="token_expired">Token Expired</option>
          </Select>
          <Input
            placeholder="Search by email"
            value={filters?.email}
            onChange={(e) => handleFilterChange('email', e?.target?.value)}
          />
          <Input
            type="date"
            placeholder="From date"
            value={filters?.dateFrom}
            onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
          />
          <Input
            type="date"
            placeholder="To date"
            value={filters?.dateTo}
            onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={handleApplyFilters}>
            <Icon name="Search" size={14} className="mr-1" />
            Apply Filters
          </Button>
          <Button size="sm" variant="outline" onClick={handleClearFilters}>
            <Icon name="X" size={14} className="mr-1" />
            Clear
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-3">
          <p className="text-sm text-error">{error}</p>
        </div>
      )}

      {/* Audit Log Timeline */}
      <div className="bg-card rounded-lg border border-border">
        <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
          {auditLogs?.length === 0 ? (
            <div className="p-8 text-center">
              <Icon name="FileText" className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No audit logs found</p>
            </div>
          ) : (
            auditLogs?.map((log) => (
              <div key={log?.id} className="p-4 hover:bg-muted/30 transition-colors">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getActionColor(log?.actionType)}`}>
                    <Icon name={getActionIcon(log?.actionType)} size={18} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-1">
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          {log?.actionType?.replace(/_/g, ' ')?.toUpperCase()}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {log?.email}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(log?.createdAt)?.toLocaleString()}
                      </span>
                    </div>
                    {log?.performedBy && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Performed by: <span className="font-medium">{log?.performedBy}</span>
                      </p>
                    )}
                    {log?.failureReason && (
                      <div className="mt-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2">
                        <p className="text-xs text-red-700 dark:text-red-300">
                          <strong>Failure Reason:</strong> {log?.failureReason}
                        </p>
                      </div>
                    )}
                    {log?.metadata && (
                      <details className="mt-2">
                        <summary className="text-xs text-primary cursor-pointer hover:underline">
                          View metadata
                        </summary>
                        <div className="mt-2 bg-muted/50 rounded p-2">
                          <pre className="text-xs font-mono text-muted-foreground overflow-x-auto">
                            {JSON.stringify(log?.metadata, null, 2)}
                          </pre>
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Export Options */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Showing {auditLogs?.length} audit log entr{auditLogs?.length !== 1 ? 'ies' : 'y'}
        </p>
        <Button size="sm" variant="outline">
          <Icon name="Download" size={14} className="mr-1" />
          Export Audit Log
        </Button>
      </div>
    </div>
  );
};

export default AuditLogPanel;
